import * as React from 'react';
import { connect } from 'react-redux';
import config from 'config';
import V3CollapsibleSection from '../../components/V3CollapsibleSection';
import V3SpecsTable from '../../components/V3SpecsTable';
import V3SpecRow from '../../components/V3SpecRow';
import V3SpecFieldWrap from '../../components/V3SpecFieldWrap';
import V3InfoBanner from '../../components/V3InfoBanner';
import V3Button from '../../components/V3Button';
import { SET_PROJECT_OPTION, GET_ENVIRONMENTAL_FACTORS, CLEAR_ENV_FACTORS_FOR, RESET_PANELS_STATE, BOM_RESET } from 'actions';
import { SET_ROOF_ROOF_PITCH, DELETE_PANELS_INSIDE_OF_ROOF } from '__editor/googleMapsRoofsSelector/components/drawingManager/drawingManagerActions';
import { SET_TILTED_ROOF } from '__editor/panelsEditor/components/tiltedRoof/tiltedRoofActions';
import { ROOF_PITCH } from '__editor/panelsEditor/components/tiltedRoof/roofPitch';
import { state as getState } from '__common/store';
import { asce_705, asce_710, asce_716, asce_722, isASCE722, isASCE716or722 } from '__common/constants/buildingCodes';
import { windExposureOption } from 'projectDesign/components/projectConfiguration/fields/types/windExposure';
import { RiskCategory } from 'projectDesign/components/projectConfiguration/fields/riskCategory';
import { isNxtHorizon, isSolarMount, isCommercialProduct } from '__common/constants/products';
import { getWindSpeedLimits, getSnowLoadLimits } from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/utils/validateProject';
import { validateZipCode } from 'projectDesign/components/projectConfiguration/utils/validation';
import { isMetricUnit } from 'engineering/components/engineeringProjectDocuments/utils/unitTypes';

type OwnProps = {};

type Props = OwnProps & {
  dispatch: Function;
  envFactorsLoading: boolean;
  productId: number | undefined;
  inputUnit: number | undefined;
  // Individual field props (scalars) so react-redux's shallow equality check
  // triggers a re-render when any one changes. Returning the whole
  // projectEnvConfig object as a single prop breaks under the reducer's
  // in-place mutation of SET_PROJECT_OPTION — the object reference stays
  // the same and connect skips the re-render.
  zipcode: string | undefined;
  buildingCode: number | undefined;
  elevation: number | undefined;
  windExposure: string | undefined;
  riskCategory: number | undefined;
  windSpeed: number | undefined;
  snowLoad: number | undefined;
  seismicSs: number | undefined;
  seismicS1: number | undefined;
  seismicSds: number | undefined;
  seismicSd1: number | undefined;
  tornadoSpeed: number | undefined;
  lat: number | undefined;
  lng: number | undefined;
  filledCount: number;
};

const LocationIcon = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ width: 14, height: 14, minWidth: 14, minHeight: 14 }}>
    <path d="M7 1C4.79 1 3 2.79 3 5c0 3.25 4 8 4 8s4-4.75 4-8c0-2.21-1.79-4-4-4zm0 5.5A1.5 1.5 0 115.5 5 1.5 1.5 0 017 6.5z" fill="currentColor"/>
  </svg>
);

type LocationState = {
  pendingBuildingCode: number | null;
};

// Minimum roof pitch (degrees) allowed for SM/NXT under ASCE 7-16 / 7-22.
const MIN_PITCH_716_722 = 9;

const BUILDING_CODE_OPTIONS = [asce_722, asce_716, asce_710, asce_705];

const buildingCodeLabel = (value: number | undefined): string => {
  const opt = BUILDING_CODE_OPTIONS.find((o) => o.value === value);
  return opt ? opt.name : '—';
};

const windExposureLabel = (value: string | undefined): string => {
  return value ? `Exp ${value}` : '—';
};

class LocationSection extends React.Component<Props, LocationState> {
  state: LocationState = { pendingBuildingCode: null };

  computeErrors = (): {
    zipcode?: string;
    windSpeed?: string;
    snowLoad?: string;
    seismicSs?: string;
    seismicS1?: string;
    seismicSds?: string;
    tornadoSpeed?: string;
  } => {
    const { zipcode, buildingCode, riskCategory, windSpeed, snowLoad, seismicSs, seismicS1, seismicSds, tornadoSpeed, inputUnit } = this.props;
    const errs: {
      zipcode?: string;
      windSpeed?: string;
      snowLoad?: string;
      seismicSs?: string;
      seismicS1?: string;
      seismicSds?: string;
      tornadoSpeed?: string;
    } = {};

    if (zipcode !== undefined && zipcode !== null && zipcode !== '') {
      if (!validateZipCode(zipcode)) {
        errs.zipcode = 'Please enter a valid ZIP Code.';
      }
    }

    if (windSpeed !== undefined && windSpeed !== null) {
      const limits = getWindSpeedLimits();
      if (windSpeed < limits.lowerLimit || windSpeed > limits.upperLimit) {
        errs.windSpeed = `Wind speed must lie between ${limits.lowerLimit} and ${limits.upperLimit} mph`;
      }
    }

    if (snowLoad !== undefined && snowLoad !== null) {
      const limits = getSnowLoadLimits();
      if (snowLoad < limits.lowerLimit || snowLoad > limits.upperLimit) {
        errs.snowLoad = `Snow load must lie between ${limits.lowerLimit} and ${limits.upperLimit} psf`;
      }
    }

    if (seismicSs !== undefined && seismicSs !== null) {
      if (seismicSs <= 0 || seismicSs > 3.1) {
        errs.seismicSs = 'Seismic Ss must be greater than 0 and less than or equal to 3.1';
      }
    }

    if (seismicS1 !== undefined && seismicS1 !== null) {
      if (seismicS1 < 0 || seismicS1 > 3.1) {
        errs.seismicS1 = 'Seismic S1 must lie between 0 and 3.1';
      }
    }

    if (isASCE722(buildingCode || 0) && seismicSds !== undefined && seismicSds !== null) {
      if (seismicSds < 0.01 || seismicSds > 3.1) {
        errs.seismicSds = 'Seismic Sds must lie between 0.01 and 3.1';
      }
    }

    if (isASCE722(buildingCode || 0) && (riskCategory === RiskCategory.III || riskCategory === RiskCategory.IV) && tornadoSpeed !== undefined && tornadoSpeed !== null) {
      const isMetric = isMetricUnit(inputUnit);
      const maxTornado = isMetric ? 222 : 138;
      if (tornadoSpeed < 0 || tornadoSpeed > maxTornado) {
        errs.tornadoSpeed = `Tornado speed must lie between 0 and ${maxTornado}`;
      }
    }

    return errs;
  };

  componentDidMount() {
    this.applyLiteDefaults();
  }

  componentDidUpdate() {
    this.applyLiteDefaults();
  }

  // Resolve lat/lng from a zipcode via geocode_from_zipcode, then re-fetch env
  // factors. Only ever triggered by a user-driven field edit (ZIP / building
  // code / risk category) — never on landing. The Project model doesn't persist
  // lat/lng, so on reload coords come back undefined; this derives them when an
  // edit needs them.
  geocodeThenFetch = (zipcode: string, overrides: { building_code?: number; risk_category?: number } = {}) => {
    if (!zipcode) return;
    const apiBase = (config as any).apiUrl;
    const url = `${window.location.protocol}//${apiBase}api/v1/geocode_from_zipcode/${zipcode}/`;
    fetch(url, { headers: { Accept: 'application/json' } })
      .then((resp) => resp.ok ? resp.json() : null)
      .then((body: any) => {
        if (!body) return;
        // Backend returns {lat, lng, address, zipcode, ...}.
        if (typeof body.lat === 'number') this.setField('lat', body.lat);
        if (typeof body.lng === 'number') this.setField('lng', body.lng);

        if (typeof body.lat === 'number' && typeof body.lng === 'number') {
          this.refetchFactors({ ...overrides, zipcode, lat: body.lat, lng: body.lng });
        }
      })
      .catch(() => {/* swallow — env factors just won't re-fetch */});
  };


  // Only wind_exposure has a Lite-spec default that isn't covered by product
  // defaults or env-factors fetch (docs/lite_default_values.md: 'C').
  // Other fields (zipcode, building_code, wind_speed, etc.) are either
  // user-entered, GET-loaded, or filled by the env-factors API.
  applyLiteDefaults = () => {
    const { windExposure } = this.props;
    if (windExposure === undefined || windExposure === null || windExposure === '') {
      this.setField('wind_exposure', 'C');
    }
  };

  setField = (field: string, value: any) => {
    this.props.dispatch(SET_PROJECT_OPTION(field, value));
  };

  // Re-fetch env factors using whatever lat/lng/productId we have. Called
  // after fields that affect the API response (zipcode, building_code,
  // risk_category) are edited.
  refetchFactors = (overrides: {
    zipcode?: string;
    building_code?: number;
    risk_category?: number;
    lat?: number;
    lng?: number;
  } = {}) => {
    const { zipcode: currentZip, buildingCode: currentBC, riskCategory: currentRC, lat: currentLat, lng: currentLng, productId, dispatch } = this.props;
    const zipcode = overrides.zipcode ?? currentZip;
    const buildingCode = overrides.building_code ?? currentBC;
    const riskCategory = overrides.risk_category ?? currentRC;
    const lat = overrides.lat ?? currentLat;
    const lng = overrides.lng ?? currentLng;

    if (!zipcode || !buildingCode || !productId) return;
    // Coords aren't persisted server-side, so an edit on the design page may
    // land here without them. Derive from the zip via geocode, then fetch.
    if (lat === undefined || lat === null || lng === undefined || lng === null) {
      this.geocodeThenFetch(zipcode, { building_code: buildingCode, risk_category: riskCategory });
      return;
    }
    dispatch(GET_ENVIRONMENTAL_FACTORS(lat, lng, zipcode, buildingCode, productId, riskCategory));
  };

  onZipBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const next = e.target.value.trim();
    if (!next || next === this.props.zipcode) return;

    // Clear stale coordinates/factors, then geocode the new zip and re-fetch.
    this.props.dispatch(CLEAR_ENV_FACTORS_FOR());
    this.setField('lat', undefined);
    this.setField('lng', undefined);
    this.setField('zipcode', next);
    if (validateZipCode(next)) {
      this.geocodeThenFetch(next);
    }
  };

  onBuildingCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = Number(e.target.value);
    const { productId, buildingCode } = this.props;
    // SM/NXT roof pitch starts at 9° under ASCE 7-16 / 7-22. When switching up
    // from 7-05 / 7-10 to one of those codes, any panels drawn on a roof with a
    // pitch below 9° must be cleared and the pitch bumped to 9°. Warn first.
    const isSmOrNxt = isSolarMount(productId || 0) || isNxtHorizon(productId || 0);
    const switchingToHigherCode = isASCE716or722(next) && !isASCE716or722(buildingCode || 0);
    if (isSmOrNxt && switchingToHigherCode && this.hasPanelsBelowMinPitch()) {
      this.setState({ pendingBuildingCode: next });
      return;
    }
    this.applyBuildingCode(next);
  };

  applyBuildingCode = (next: number) => {
    this.setField('building_code', next);
    this.refetchFactors({ building_code: next });
  };

  // True when any roof has panels drawn on a pitch below the 7-16 / 7-22 min.
  hasPanelsBelowMinPitch = (): boolean => {
    const s = getState() as any;
    const roofs = s.drawingManager && s.drawingManager.roofs;
    if (!roofs) return false;
    return Object.keys(roofs).some((roofId) => {
      const roof = roofs[roofId];
      if (!roof || !roof.panels || roof.panels.length === 0) return false;
      const pitchDeg = ROOF_PITCH[roof.roofPitch] ?? Number(roof.roofPitch);
      return !isNaN(pitchDeg) && pitchDeg < MIN_PITCH_716_722;
    });
  };

  confirmBuildingCodeChange = () => {
    const { pendingBuildingCode } = this.state;
    this.setState({ pendingBuildingCode: null }, () => {
      if (pendingBuildingCode !== null) {
        this.clearPanelsAndRaisePitch();
        this.applyBuildingCode(pendingBuildingCode);
      }
    });
  };

  cancelBuildingCodeChange = () => {
    this.setState({ pendingBuildingCode: null });
  };

  // Clear every roof's panels and raise any sub-9° pitch to 9°.
  clearPanelsAndRaisePitch = () => {
    this.props.dispatch(RESET_PANELS_STATE());
    this.props.dispatch(BOM_RESET());
    const s = getState() as any;
    const roofs = s.drawingManager && s.drawingManager.roofs;
    if (roofs) {
      Object.keys(roofs).forEach((roofId) => {
        this.props.dispatch(DELETE_PANELS_INSIDE_OF_ROOF(Number(roofId)));
        const roof = roofs[roofId];
        const pitchDeg = ROOF_PITCH[roof.roofPitch] ?? Number(roof.roofPitch);
        if (isNaN(pitchDeg) || pitchDeg < MIN_PITCH_716_722) {
          this.props.dispatch(SET_ROOF_ROOF_PITCH(Number(roofId), String(MIN_PITCH_716_722)));
        }
      });
    }
    this.props.dispatch(SET_TILTED_ROOF(String(MIN_PITCH_716_722)));
    this.setField('roof_pitch', String(MIN_PITCH_716_722));
  };

  onRiskCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = Number(e.target.value);
    this.setField('risk_category', next);
    this.refetchFactors({ risk_category: next });
  };

  onNumericField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    this.setField(field, raw === '' ? undefined : Number(raw));
  };

  onStringField = (field: string) => (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    this.setField(field, e.target.value);
  };

  render() {
    const {
      zipcode, buildingCode, elevation, windExposure, riskCategory,
      windSpeed, snowLoad, seismicSs, seismicS1, seismicSds, tornadoSpeed,
      envFactorsLoading, filledCount, productId
    } = this.props;
    const errors = this.computeErrors();
    const placeholder = envFactorsLoading ? '…' : '—';
    const summary = (
      <React.Fragment>
        <span className="v3-sum-chip">{zipcode || placeholder}</span>
        {filledCount > 0 && (
          <span className="v3-sum-more">+{filledCount} fields</span>
        )}
      </React.Fragment>
    );

    return (
      <React.Fragment>
      <V3CollapsibleSection
        title="Location"
        icon={LocationIcon}
        iconVariant="green"
        defaultOpen={false}
        summarySlot={summary}
        summaryPosition="inline"
        infoBanner={
          <V3InfoBanner variant="info">
            Auto-filled from Step 2 address &middot; Edit any field directly
          </V3InfoBanner>
        }
      >
        <V3SpecsTable flush={true}>
          <V3SpecRow label="ZIP Code">
            <V3SpecFieldWrap autofilled={true} className="zip-wrap">
              <input
                className={`v3-spec-inline-input${errors.zipcode ? ' v3-spec-val-error' : ''}`}
                type="text"
                maxLength={5}
                placeholder={placeholder}
                style={{ width: 55 }}
                defaultValue={zipcode || ''}
                key={zipcode || ''}
                onBlur={this.onZipBlur}
              />
            </V3SpecFieldWrap>
          </V3SpecRow>
          {errors.zipcode && (
            <V3SpecRow label="">
              <span className="v3-specs-row-err">{errors.zipcode}</span>
            </V3SpecRow>
          )}
          <V3SpecRow label="Building Code">
            <V3SpecFieldWrap>
              <select
                className="v3-spec-inline-select"
                style={{ width: 90 }}
                value={buildingCode || ''}
                onChange={this.onBuildingCodeChange}
              >
                <option value="" disabled>Select</option>
                {BUILDING_CODE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
            </V3SpecFieldWrap>
          </V3SpecRow>
          <V3SpecRow label="Elevation">
            <V3SpecFieldWrap autofilled={true}>
              <input
                className="v3-spec-inline-input"
                type="number"
                placeholder={placeholder}
                style={{ width: 50 }}
                value={elevation ?? ''}
                onChange={this.onNumericField('elevation')}
              />
              <span className="v3-spec-unit">ft</span>
            </V3SpecFieldWrap>
          </V3SpecRow>
          <V3SpecRow label="Wind Exposure">
            <V3SpecFieldWrap>
              <select
                className="v3-spec-inline-select"
                style={{ width: 100 }}
                value={windExposure || ''}
                onChange={this.onStringField('wind_exposure')}
              >
                <option value="" disabled>Select</option>
                {Object.values(windExposureOption).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
            </V3SpecFieldWrap>
          </V3SpecRow>
          {/* Risk Category is only valid for ASCE 7-16 and ASCE 7-22 building codes.
              Backend strips it from save payloads for other codes (views.py:366-367).
              Matches the existing pattern at line 318 for Seismic Sds (inverse). */}
          {productId && (
            isCommercialProduct(productId) ||
            ((isNxtHorizon(productId) || isSolarMount(productId)) && isASCE716or722(buildingCode || 0))
          ) && (
            <V3SpecRow label="Risk Category">
              <V3SpecFieldWrap>
                <select
                  className="v3-spec-inline-select"
                  style={{ width: 60 }}
                  value={riskCategory ?? ''}
                  onChange={this.onRiskCategoryChange}
                >
                  <option value="" disabled>Select</option>
                  <option value={RiskCategory.I}>I</option>
                  <option value={RiskCategory.II}>II</option>
                  <option value={RiskCategory.III}>III</option>
                  <option value={RiskCategory.IV}>IV</option>
                </select>
              </V3SpecFieldWrap>
            </V3SpecRow>
          )}
          {/* Tornado Speed is only required for ASCE 7-22 with risk category III or IV.
              Backend serves tornado factor from /env-factors API; field is autofilled
              when available. Hide for other code/risk combos per legacy pattern
              (constraints.ts:22-24, ecoFoot2PlusConfig.ts:189). */}
          {isASCE722(buildingCode || 0)
            && (riskCategory === RiskCategory.III || riskCategory === RiskCategory.IV) && (
            <React.Fragment>
              <V3SpecRow label="Tornado Speed">
                <V3SpecFieldWrap autofilled={true}>
                  <input
                    className={`v3-spec-inline-input${errors.tornadoSpeed ? ' v3-spec-val-error' : ''}`}
                    type="number"
                    placeholder={placeholder}
                    style={{ width: 46 }}
                    value={tornadoSpeed ?? ''}
                    onChange={this.onNumericField('tornado_speed')}
                  />
                  <span className="v3-spec-unit">mph</span>
                </V3SpecFieldWrap>
              </V3SpecRow>
              {errors.tornadoSpeed && (
                <V3SpecRow label="">
                  <span className="v3-specs-row-err">{errors.tornadoSpeed}</span>
                </V3SpecRow>
              )}
            </React.Fragment>
          )}
          <V3SpecRow label="Basic Wind Speed">
            <V3SpecFieldWrap autofilled={true}>
              <input
                className={`v3-spec-inline-input${errors.windSpeed ? ' v3-spec-val-error' : ''}`}
                type="number"
                placeholder={placeholder}
                style={{ width: 46 }}
                value={windSpeed ?? ''}
                onChange={this.onNumericField('wind_speed')}
              />
              <span className="v3-spec-unit">mph</span>
            </V3SpecFieldWrap>
          </V3SpecRow>
          {errors.windSpeed && (
            <V3SpecRow label="">
              <span className="v3-specs-row-err">{errors.windSpeed}</span>
            </V3SpecRow>
          )}
          <V3SpecRow label="Ground Snow Load">
            <V3SpecFieldWrap autofilled={true}>
              <input
                className={`v3-spec-inline-input${errors.snowLoad ? ' v3-spec-val-error' : ''}`}
                type="number"
                placeholder={placeholder}
                style={{ width: 46 }}
                value={snowLoad ?? ''}
                onChange={this.onNumericField('snow_load')}
              />
              <span className="v3-spec-unit">psf</span>
            </V3SpecFieldWrap>
          </V3SpecRow>
          {errors.snowLoad && (
            <V3SpecRow label="">
              <span className="v3-specs-row-err">{errors.snowLoad}</span>
            </V3SpecRow>
          )}
          <V3SpecRow label="Seismic Factor (Ss)">
            <V3SpecFieldWrap autofilled={true}>
              <input
                className={`v3-spec-inline-input${errors.seismicSs ? ' v3-spec-val-error' : ''}`}
                type="number"
                step="0.001"
                placeholder={placeholder}
                style={{ width: 68 }}
                value={seismicSs ?? ''}
                onChange={this.onNumericField('seismic_ss')}
              />
            </V3SpecFieldWrap>
          </V3SpecRow>
          {errors.seismicSs && (
            <V3SpecRow label="">
              <span className="v3-specs-row-err">{errors.seismicSs}</span>
            </V3SpecRow>
          )}
          <V3SpecRow label="Seismic Factor (S1)">
            <V3SpecFieldWrap autofilled={true}>
              <input
                className={`v3-spec-inline-input${errors.seismicS1 ? ' v3-spec-val-error' : ''}`}
                type="number"
                step="0.001"
                placeholder={placeholder}
                style={{ width: 68 }}
                value={seismicS1 ?? ''}
                onChange={this.onNumericField('seismic_s1')}
              />
            </V3SpecFieldWrap>
          </V3SpecRow>
          {errors.seismicS1 && (
            <V3SpecRow label="">
              <span className="v3-specs-row-err">{errors.seismicS1}</span>
            </V3SpecRow>
          )}
          {/* Sds is auto-derived from Ss (and S1, for risk-cat 4) under
              ASCE 7-16 / 7-22 — see backend reports/calculations/common/seismic_factors.py.
              Surfacing it as a user-editable field is misleading because the
              backend recomputes it on every BOM/report run. Hide for those
              codes; older codes (ASCE 7-05/7-10) still need a manual entry. */}
          {isASCE722(buildingCode || 0) && (
            <React.Fragment>
              <V3SpecRow label="Seismic (Sds)">
                <V3SpecFieldWrap autofilled={true}>
                  <input
                    className={`v3-spec-inline-input${errors.seismicSds ? ' v3-spec-val-error' : ''}`}
                    type="number"
                    step="0.001"
                    placeholder={placeholder}
                    style={{ width: 68 }}
                    value={seismicSds ?? ''}
                    onChange={this.onNumericField('seismic_sds')}
                  />
                </V3SpecFieldWrap>
              </V3SpecRow>
              {errors.seismicSds && (
                <V3SpecRow label="">
                  <span className="v3-specs-row-err">{errors.seismicSds}</span>
                </V3SpecRow>
              )}
            </React.Fragment>
          )}
        </V3SpecsTable>
      </V3CollapsibleSection>
      {this.state.pendingBuildingCode !== null && (
        <div className="v3-modal-overlay" onClick={this.cancelBuildingCodeChange}>
          <div className="v3-modal-card" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="v3-modal-top">
              <div className="v3-modal-step-label">WARNING</div>
              <div className="v3-modal-title">Change Building Code?</div>
            </div>
            <div className="v3-modal-body">
              <div className="v3-modal-subtitle">
                ASCE 7-16 / 7-22 requires a minimum roof pitch of 9°. Continuing will
                clear all panels drawn on roofs below 9°.
                This cannot be undone.
              </div>
            </div>
            <div className="v3-modal-footer">
              <V3Button variant="ghost" size="md" onClick={this.cancelBuildingCodeChange}>
                Cancel
              </V3Button>
              <V3Button variant="primary" size="md" onClick={this.confirmBuildingCodeChange}>
                Continue
              </V3Button>
            </div>
          </div>
        </div>
      )}
      </React.Fragment>
    );
  }
}

function mapStateToProps(appState: appState) {
  const pc = (appState as any).projectConfiguration || {};
  const env = pc.projectEnvConfig || {};
  const hasVal = (v: any) => v !== undefined && v !== null && v !== '';
  const filledCount = [
    env.building_code, env.wind_speed, env.snow_load, env.elevation,
    env.wind_exposure, env.risk_category, env.seismic_ss, env.seismic_s1,
    env.seismic_sds, env.seismic_sd1, env.tornado_speed,
  ].filter(hasVal).length;
  return {
    envFactorsLoading: !!pc.envFactorsLoading,
    productId: pc.productId,
    inputUnit: env.input_unit,
    zipcode: env.zipcode,
    buildingCode: env.building_code,
    elevation: env.elevation,
    windExposure: env.wind_exposure,
    riskCategory: env.risk_category,
    windSpeed: env.wind_speed,
    snowLoad: env.snow_load,
    seismicSs: env.seismic_ss,
    seismicS1: env.seismic_s1,
    seismicSds: env.seismic_sds,
    seismicSd1: env.seismic_sd1,
    tornadoSpeed: env.tornado_speed,
    lat: env.lat,
    lng: env.lng,
    filledCount,
  };
}

export default connect(mapStateToProps)(LocationSection);
